import { getStartEpoch } from '@thoughtspot/custom-calendar-service';
import { convertToMilliSeconds, convertToSeconds } from '@thoughtspot/date-format-util';
import { getFormattedValueForNumericColumnJson, TooltipLabelType, } from '@thoughtspot/formatter-service';
import { colorPaletteMappings } from '@thoughtspot/radiant-react/widgets/color';
import { hasCustomCalendar } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getIncompleteThreshold } from '../components/kpi/kpi-utils';
import { getXAxisColumns, isXAxisOrdinalBased, } from '../core/chart-model-utils';
import { getAllColumnsConfig, removeIncompleteDataIfPresent, removeLastIfNullAndEqualLength, } from '../data/data-util';
import { getEnvFlags, getTranslatedTextCallback } from '../env';
import { HighchartDataModel } from '../highcharts/highchart-data-model';
import { createLogger } from '../utils/logger';
const logger = createLogger('insight-data-model');
const FORECAST_LINE_STYLE = 'Dot';
const FORECAST_LINE_COLOR = colorPaletteMappings.gray[60].value;
const PARENT_SERIES_COLOR = colorPaletteMappings.blue[60].value;
export class InsightChartDataModel extends HighchartDataModel {
    constructor(_chartModel, _data, _queryDefinitions, _options, chartOptions, getEventMap) {
        super(_chartModel, _data, _queryDefinitions, _options, chartOptions, getEventMap);
        this._chartModel = _chartModel;
        this._data = _data;
        this._queryDefinitions = _queryDefinitions;
        this._options = _options;
        if (getEnvFlags().isSpotIQV2Enabled) {
            this.processInsight(_chartModel);
        }
    }
    filterDataByThreshold(threshold) {
        this.series.forEach((serie, index) => {
            if (index === 0) {
                serie.data = serie.data.filter(point => point.x <= threshold);
            }
        });
    }
    processInsight(chartModel) {
        var _a, _b, _c, _d, _e, _f, _g;
        this.series[0].color = PARENT_SERIES_COLOR;
        if ((_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.cortexData) === null || _a === void 0 ? void 0 : _a.forecasts) {
            const yAxisConfig = (_d = (_c = (_b = chartModel === null || chartModel === void 0 ? void 0 : chartModel.config) === null || _b === void 0 ? void 0 : _b.axisConfig) === null || _c === void 0 ? void 0 : _c[0].y) === null || _d === void 0 ? void 0 : _d[0];
            const xAxisColumn = _.first(getXAxisColumns(chartModel));
            const yAxisId = yAxisConfig.id;
            const xAxisIndex = chartModel === null || chartModel === void 0 ? void 0 : chartModel.cortexData.forecasts.matrix.columnInfos.findIndex(_ => _.columnType === 'TIME');
            const yAxisIndex = chartModel === null || chartModel === void 0 ? void 0 : chartModel.cortexData.forecasts.matrix.columnInfos.findIndex(_ => _.columnType === 'TARGET');
            const upperBoundIndex = chartModel === null || chartModel === void 0 ? void 0 : chartModel.cortexData.forecasts.matrix.columnInfos.findIndex(_ => _.columnType === 'TARGET_UPPER');
            const lowerBoundIndex = chartModel === null || chartModel === void 0 ? void 0 : chartModel.cortexData.forecasts.matrix.columnInfos.findIndex(_ => _.columnType === 'TARGET_LOWER');
            const sortInfo = ((_e = chartModel === null || chartModel === void 0 ? void 0 : chartModel.sortOrder) === null || _e === void 0 ? void 0 : _e.length)
                ? chartModel === null || chartModel === void 0 ? void 0 : chartModel.sortOrder
                : chartModel === null || chartModel === void 0 ? void 0 : chartModel.sortInfo;
            const isXAxisSortAsc = (_g = (_f = sortInfo === null || sortInfo === void 0 ? void 0 : sortInfo.find(_ => _.columnId === xAxisColumn.id)) === null || _f === void 0 ? void 0 : _f.sortAscending) !== null && _g !== void 0 ? _g : true;
            const lastChartPoint = removeLastIfNullAndEqualLength(chartModel);
            const incompleteThreshold = getIncompleteThreshold(chartModel);
            const incompleteThresholdInSeconds = convertToSeconds(incompleteThreshold);
            let lastX = isXAxisSortAsc
                ? lastChartPoint[0].dataValue[lastChartPoint[0].dataValue.length - 1]
                : lastChartPoint[0].dataValue[0];
            const isOrdinalXAxis = isXAxisOrdinalBased(chartModel);
            if (isOrdinalXAxis) {
                lastX = lastChartPoint[0].dataValue.length - 1;
            }
            else if (hasCustomCalendar(xAxisColumn)) {
                lastX = getStartEpoch(lastX);
            }
            let lastY = isXAxisSortAsc
                ? lastChartPoint[1].dataValue[lastChartPoint[1].dataValue.length - 1]
                : lastChartPoint[1].dataValue[0];
            if (getEnvFlags().isForecastIncompletePointsHidden &&
                lastX > incompleteThresholdInSeconds) {
                const filteredPoints = lastChartPoint[0].dataValue.filter(point => {
                    const x = hasCustomCalendar(xAxisColumn)
                        ? getStartEpoch(point)
                        : point;
                    return x <= incompleteThresholdInSeconds;
                });
                lastX = isXAxisSortAsc
                    ? filteredPoints[filteredPoints.length - 1]
                    : filteredPoints[0];
                if (isOrdinalXAxis) {
                    lastX = filteredPoints.length - 1;
                }
                else if (hasCustomCalendar(xAxisColumn)) {
                    lastX = getStartEpoch(lastX);
                }
                lastY = isXAxisSortAsc
                    ? lastChartPoint[1].dataValue[filteredPoints.length - 1]
                    : lastChartPoint[1].dataValue[0];
                removeIncompleteDataIfPresent(chartModel, isXAxisSortAsc, hasCustomCalendar(xAxisColumn), incompleteThresholdInSeconds);
                this.filterDataByThreshold(incompleteThreshold);
            }
            const _seriesMap = {
                forecasting: {
                    blinkSeriesId: 'Forecast',
                    name: getTranslatedTextCallback()('FORECAST_TITLE'),
                    visible: true,
                    valueColumnIdentifier: yAxisId,
                    data: [
                        {
                            colorRawValues: [],
                            additionalTooltipData: [],
                            tooltipDataContent: {},
                            x: isOrdinalXAxis
                                ? lastX
                                : convertToMilliSeconds(lastX),
                            xRawValues: [convertToMilliSeconds(lastX)],
                            y: lastY,
                            yRawValues: [lastY],
                            label: lastY,
                        },
                    ],
                    color: FORECAST_LINE_COLOR,
                    dashStyle: FORECAST_LINE_STYLE,
                },
            };
            const columnConfig = getAllColumnsConfig(chartModel, this)[yAxisId];
            const sampleXRawValue = this.xValueToRawValues[this.xValueToRawValues.length - 1][0];
            const sampleYRawValue = this.yValueToRawValues[this.yValueToRawValues.length - 1][0];
            chartModel === null || chartModel === void 0 ? void 0 : chartModel.cortexData.forecasts.matrix.rows.forEach((_forecast, index) => {
                const lowerBoundVal = getFormattedValueForNumericColumnJson(_forecast.columns[lowerBoundIndex].doubleVal, columnConfig);
                const upperBoundVal = getFormattedValueForNumericColumnJson(_forecast.columns[upperBoundIndex].doubleVal, columnConfig);
                _seriesMap.forecasting.data.push({
                    colorRawValues: [],
                    x: isOrdinalXAxis
                        ? lastX + index + 1
                        : convertToMilliSeconds(_forecast.columns[xAxisIndex].intVal),
                    xRawValues: [
                        convertToMilliSeconds(_forecast.columns[xAxisIndex].intVal),
                    ],
                    y: _forecast.columns[yAxisIndex].doubleVal,
                    yRawValues: [_forecast.columns[yAxisIndex].doubleVal],
                    label: _forecast.columns[yAxisIndex].doubleVal,
                    additionalTooltipData: [
                        {
                            label: getTranslatedTextCallback()('FORECAST_POINT_BOUND'),
                            value: `${lowerBoundVal} to ${upperBoundVal}`,
                            labelType: TooltipLabelType.TOOLTIP_CONFIG_LABEL,
                        },
                    ],
                    tooltipDataContent: {},
                });
                this.xValueToRawValues.push([
                    { ...sampleXRawValue, value: convertToMilliSeconds(_forecast.columns[xAxisIndex].intVal) },
                ]);
                this.yValueToRawValues.push([
                    { ...sampleYRawValue, value: _forecast.columns[yAxisIndex].doubleVal },
                ]);
            });
            Object.keys(_seriesMap).forEach(_seriesName => {
                const _series = _seriesMap[_seriesName];
                this._seriesMap[_series.blinkSeriesId] = _series;
                this.series.push(_series);
                this.uniqueLegendValues.push(_series.blinkSeriesId);
                this.longestSerieNames.push(_series.name);
            });
            this.series[0].zIndex = 1;
        }
    }
    get hasNoData() {
        return _.isEmpty(this._data);
    }
    getHighchartDatamodel() {
        return this._highchartDataModel;
    }
}
//# sourceMappingURL=insight-data-model.js.map